/**
 * Dashboard — Strategy-Snapshot statt App-Launchpad.
 *
 * Wenn der User schon Workshops hat, zeigt das Dashboard den zuletzt
 * aktualisierten Workshop prominent als Hero-Karte inklusive Modul-
 * Fortschritt und „Weiter mit Modul N"-CTA. Weitere Workshops kommen
 * als kompakte Reihe darunter. Quick-Actions (neuer Workshop, Brand-
 * Brief, Bibliothek) sind klein, unaufdringlich unten.
 *
 * Wenn (noch) keine Workshops existieren, übernimmt eine Empty-State-
 * Karte den ganzen Bereich und führt zum Workshop-Anlegen.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Download, FileText, Library, Plus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { downloadWorkshopPdf, listWorkshops, type Workshop } from "@/api/workshops";
import { useAuthStore } from "@/auth/store";
import { moduleRoute } from "@/components/workshop/moduleStatus";
import { useConfettiOnce } from "@/components/workshop/useConfettiOnce";
import { useWorkshopProgress } from "@/components/workshop/useWorkshopProgress";
import { WorkshopProgress } from "@/components/workshop/WorkshopProgress";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

const STATUS_COLORS: Record<Workshop["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  in_progress: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  completed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  archived: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
};

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const { data: workshops, isLoading } = useQuery({
    queryKey: ["workshops"],
    queryFn: () => listWorkshops(),
  });

  const sorted = [...(workshops ?? [])].sort((a, b) =>
    (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at),
  );
  const hero = sorted[0];
  const rest = sorted.slice(1, 5);

  return (
    <div className="container py-8">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {t("dashboard.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("dashboard.welcome", { name: user?.full_name ?? "" })}
        </p>
      </header>

      {isLoading ? (
        <DashboardSkeleton />
      ) : !hero ? (
        <EmptyStateCard />
      ) : (
        <div className="space-y-8">
          <HeroWorkshopCard workshop={hero} />

          {rest.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-end justify-between">
                <h2 className="font-display text-lg font-semibold">
                  {t("dashboard.more_workshops")}
                </h2>
                <Link
                  to="/workshops"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {t("dashboard.show_all")} →
                </Link>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {rest.map((w) => (
                  <WorkshopMiniCard key={w.id} workshop={w} />
                ))}
              </div>
            </section>
          )}

          <QuickActions />
        </div>
      )}
    </div>
  );
}

function HeroWorkshopCard({ workshop }: { workshop: Workshop }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { progress, nextModule } = useWorkshopProgress(workshop.id);
  const completed = Object.values(progress).filter(
    (p) => p.status === "completed",
  ).length;
  const allDone = completed === 5;

  // Konfetti einmalig auch hier — die Reveal-Karte in WorkshopDetail
  // verwendet denselben Storage-Key, also feuert es nur einmal pro
  // Workshop, egal wo der User es zuerst sieht.
  useConfettiOnce(workshop.id, allDone);

  const exportMut = useMutation({
    mutationFn: () => downloadWorkshopPdf(workshop.id, workshop.name),
    onError: () =>
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("workshops.export_failed"),
      }),
  });

  const continueHref = nextModule
    ? moduleRoute(workshop.id, nextModule)
    : `/workshops/${workshop.id}`;
  const continueLabel = !nextModule
    ? t("dashboard.review_workshop")
    : completed === 0
      ? t("dashboard.start_workshop")
      : t("dashboard.continue_with_module", {
          name: t(`workshops.modules.${nextModule}`),
        });

  const gradientClass = allDone
    ? "bg-gradient-to-r from-primary/15 via-primary/8 to-accent/10"
    : "bg-gradient-to-r from-primary/8 via-primary/3 to-transparent";

  return (
    <Card className={`overflow-hidden ${allDone ? "border-primary/30" : ""}`}>
      <div className={gradientClass}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                {allDone
                  ? t("dashboard.your_workshop_done")
                  : t("dashboard.your_active_workshop")}
              </p>
              <CardTitle className="mt-1 truncate font-display text-2xl">
                <Link to={`/workshops/${workshop.id}`} className="hover:underline">
                  {workshop.name}
                </Link>
              </CardTitle>
              <CardDescription>
                {t("dashboard.last_updated", {
                  date: new Date(
                    workshop.updated_at ?? workshop.created_at,
                  ).toLocaleString(i18n.language),
                })}
              </CardDescription>
            </div>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[workshop.status]}`}
            >
              {t(`workshops.status.${workshop.status}`)}
            </span>
          </div>
        </CardHeader>
      </div>
      <CardContent className="space-y-4 pt-4">
        <WorkshopProgress workshopId={workshop.id} />
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          {allDone ? (
            <Button
              size="lg"
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending}
            >
              <Download className="mr-2 h-4 w-4" />
              {exportMut.isPending
                ? t("workshops.exporting")
                : t("workshops.export_pdf")}
            </Button>
          ) : (
            <Button asChild>
              <Link to={continueHref}>
                {continueLabel}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
          <Button asChild variant="ghost" size="sm">
            <Link to={`/workshops/${workshop.id}`}>
              {t("dashboard.open_overview")}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkshopMiniCard({ workshop }: { workshop: Workshop }) {
  const { t, i18n } = useTranslation();
  return (
    <Card className="transition-colors hover:bg-muted/40">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/workshops/${workshop.id}`}
            className="min-w-0 flex-1 truncate font-medium hover:underline"
          >
            {workshop.name}
          </Link>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${STATUS_COLORS[workshop.status]}`}
          >
            {t(`workshops.status.${workshop.status}`)}
          </span>
        </div>
        <WorkshopProgress
          workshopId={workshop.id}
          showSummary={false}
          className="opacity-90"
        />
        <p className="text-[11px] text-muted-foreground">
          {new Date(workshop.updated_at ?? workshop.created_at).toLocaleDateString(
            i18n.language,
            { day: "numeric", month: "short" },
          )}
        </p>
      </CardContent>
    </Card>
  );
}

function QuickActions() {
  const { t } = useTranslation();
  return (
    <section className="flex flex-wrap items-center gap-2 border-t pt-4 text-sm">
      <span className="mr-2 text-xs uppercase tracking-wider text-muted-foreground">
        {t("dashboard.quick_actions")}
      </span>
      <Button asChild variant="outline" size="sm">
        <Link to="/workshops/new">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {t("dashboard.create_workshop")}
        </Link>
      </Button>
      <Button asChild variant="outline" size="sm">
        <Link to="/workshops/brand-brief">
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          {t("dashboard.brand_brief_open")}
        </Link>
      </Button>
      <Button asChild variant="outline" size="sm">
        <Link to="/brand-briefs">
          <Library className="mr-1.5 h-3.5 w-3.5" />
          {t("dashboard.brand_briefs_library_open")}
        </Link>
      </Button>
    </section>
  );
}

function EmptyStateCard() {
  const { t } = useTranslation();
  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="font-display text-2xl">
          {t("dashboard.empty_title")}
        </CardTitle>
        <CardDescription>{t("dashboard.empty_description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/workshops/new">
            <Plus className="mr-2 h-4 w-4" />
            {t("dashboard.create_workshop")}
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/workshops/brand-brief">
            <FileText className="mr-2 h-4 w-4" />
            {t("dashboard.brand_brief_open")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-2 h-7 w-2/3" />
          <Skeleton className="mt-1 h-3 w-1/3" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-4 h-9 w-40" />
        </CardContent>
      </Card>
    </div>
  );
}
